import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { AboveTheFoldOnlyServerRender } from 'above-the-fold-only-server-render';
import { FormattedMessage } from 'react-intl';
import { isConjunctiveField } from '@bibliocommons/utils-filter';
import { withRouter } from '@bibliocommons/utils-routing';
import { bindAll } from '@bibliocommons/utils-react';
import * as Drawer from 'app/components/shared/DrawerLayout';
import { TextButton } from '@bibliocommons/deprecated-base-buttons';
import { routerShape } from '@bibliocommons/bc-prop-types';

import getToggledFilter from 'app/helpers/facets/getToggledFilter';
import formatFiltersForQuery from 'app/helpers/facets/formatFiltersForQuery';
import CheckboxField from '../CheckboxField';
import PrivacyField from '../PrivacyField';
import FormatField from '../FormatField';
import LanguageField from '../LanguageField';
import SimpleField from '../SimpleField';
import TagGroupField from '../TagGroupField';
import SingleBranchStatusField from '../SingleBranchStatusField';

import './FieldList.scss';

export class FieldList extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  handleToggleFilter(fieldId, currentFilter, override = false) {
    const { activeFilters, paginationField, router, separator, prefix, transformQuery } = this.props;

    let query = {
      ...router.query,
      [paginationField]: undefined,
      ...formatFiltersForQuery(activeFilters, separator, prefix),
      ...getToggledFilter({ fieldId, currentFilter, activeFilters, separator, prefix, override })
    };

    if (transformQuery) {
      query = transformQuery(query);
    }

    router.push({ query });
  }

  handleToggleExpandedField(fieldId, currentFilter) {
    this.handleToggleFilter(fieldId, currentFilter);
  }

  isFieldExpanded(field) {
    if (!field) return false;
    return field.get('expanded') || this.props.activeFilters.has(field.get('id'));
  }

  renderFields(fieldId) {
    const handleToggleFilter = this.props.handleToggleFilter || this.handleToggleFilter;
    const handleToggleExpandedField = this.props.handleToggleExpandedField || this.handleToggleExpandedField;

    let fieldTag;
    switch (fieldId) {
      case 'SEARCH_SCOPE': {
        fieldTag = this.props.renderSearchScope ? this.props.renderSearchScope() : null;
        break;
      }
      case 'IS_PRIVATE': {
        const field = this.props.fields.find(f => f.get('id') === fieldId);

        if (!field) {
          return null;
        }

        fieldTag = <PrivacyField handleToggleFilter={handleToggleFilter} filters={field.get('fieldFilters')} />;
        break;
      }
      case 'I_OWN_THIS': {
        const field = this.props.fields.find(f => f.get('id') === fieldId);
        const fieldFilter = field && field.get('fieldFilters').find(filter => `${filter.get('value')}` === 'true');

        if (!fieldFilter) {
          return null;
        }

        fieldTag = (
          <CheckboxField key={fieldId} fieldId={fieldId} filter={fieldFilter} handleToggleFilter={handleToggleFilter} />
        );
        break;
      }
      case 'TAGS': {
        const genreTagField = this.props.fields.find(f => f.get('id') === 'TAG_GENRE');
        const toneTagField = this.props.fields.find(f => f.get('id') === 'TAG_TONE');
        const aboutTagField = this.props.fields.find(f => f.get('id') === 'TAG_ABOUT');
        const personalTagField = this.props.fields.find(f => f.get('id') === 'TAG_PERSONAL');

        if (!(genreTagField || toneTagField || aboutTagField || personalTagField)) {
          return null;
        }
        fieldTag = (
          <AboveTheFoldOnlyServerRender skip>
            <TagGroupField
              key="TAG_GROUP"
              genreTagField={genreTagField}
              genreExpanded={this.isFieldExpanded(genreTagField)}
              toneTagField={toneTagField}
              toneExpanded={this.isFieldExpanded(toneTagField)}
              aboutTagField={aboutTagField}
              aboutExpanded={this.isFieldExpanded(aboutTagField)}
              personalTagField={personalTagField}
              personalExpanded={this.isFieldExpanded(personalTagField)}
              handleToggleFilter={handleToggleFilter}
              handleExpandField={this.props.handleExpandField}
              activeFilters={this.props.activeFilters}
              handleCloseExpandField={this.props.handleCloseExpandField}
              handleToggleExpandedField={handleToggleExpandedField}
            />
          </AboveTheFoldOnlyServerRender>
        );
        break;
      }
      case 'LANGUAGES': {
        let field = null;
        const primaryLanguage = this.props.fields.find(f => f.get('id') === 'PRIMARY_LANGUAGE');
        const language = this.props.fields.find(f => f.get('id') === 'LANGUAGE');

        if (!(primaryLanguage || language)) {
          return null;
        }

        if (!primaryLanguage && language) {
          field = language;
        }

        if (primaryLanguage && !language) {
          field = primaryLanguage;
        }

        if (field) {
          fieldTag = (
            <AboveTheFoldOnlyServerRender skip>
              <SimpleField
                key={field.get('id')}
                label="LANGUAGE.field"
                fieldId={field.get('id')}
                hasMore={field.get('hasMore')}
                filters={field.get('fieldFilters')}
                handleExpandField={this.props.handleExpandField}
                handleToggleFilter={handleToggleFilter}
                activeFilters={this.props.activeFilters}
                initialIsExpanded={this.isFieldExpanded(field)}
                handleCloseExpandField={this.props.handleCloseExpandField}
                handleToggleExpandedField={handleToggleExpandedField}
              />
            </AboveTheFoldOnlyServerRender>
          );
        } else {
          fieldTag = (
            <AboveTheFoldOnlyServerRender skip>
              <LanguageField
                key="LANGUAGES"
                primaryLanguage={primaryLanguage}
                language={language}
                handleToggleFilter={handleToggleFilter}
                handleExpandField={this.props.handleExpandField}
                handleCloseExpandField={this.props.handleCloseExpandField}
                activeFilters={this.props.activeFilters}
                handleToggleExpandedField={handleToggleExpandedField}
                transformQuery={this.props.transformQuery}
                router={this.props.router}
              />
            </AboveTheFoldOnlyServerRender>
          );
        }
        break;
      }
      case 'FORMAT': {
        const formatField = this.props.fields.find(f => f.get('id') === fieldId);

        if (!formatField) {
          return null;
        }

        fieldTag = (
          <FormatField
            key="FORMAT"
            fieldId={formatField.get('id')}
            hasMore={formatField.get('hasMore')}
            filters={formatField.get('fieldFilters')}
            handleToggleFilter={handleToggleFilter}
            activeFilters={this.props.activeFilters}
          />
        );
        break;
      }
      case 'STATUS':
        if (this.props.singleBranch) {
          const field = this.props.fields.find(f => f.get('id') === fieldId);
          if (typeof field === 'undefined') {
            return null;
          }
          fieldTag = (
            <SingleBranchStatusField
              key={field.get('id')}
              label={field.get('labelId') || `${field.get('id')}.field`}
              fieldId={field.get('id')}
              filter={field.get('fieldFilters').first()}
              handleToggleFilter={handleToggleFilter}
              activeFilters={this.props.activeFilters}
            />
          );
          break;
        }
      // STATUS must be the last case before default so it can fallthrough on non single branch libraries
      default: {
        // eslint-disable-line no-fallthrough
        const field = this.props.fields.find(f => f.get('id') === fieldId);
        if (!field || field.get('fieldFilters').size === 0) {
          return null;
        }
        fieldTag = (
          <AboveTheFoldOnlyServerRender skip>
            <SimpleField
              isConjunctive={isConjunctiveField(fieldId)}
              key={field.get('id')}
              label={field.get('labelId') || `${field.get('id')}.field`}
              fieldId={field.get('id')}
              hasMore={field.get('hasMore')}
              filters={field.get('fieldFilters')}
              handleExpandField={this.props.handleExpandField}
              handleToggleFilter={handleToggleFilter}
              activeFilters={this.props.activeFilters}
              initialIsExpanded={this.isFieldExpanded(field)}
              handleCloseExpandField={this.props.handleCloseExpandField}
              handleToggleExpandedField={handleToggleExpandedField}
            />
          </AboveTheFoldOnlyServerRender>
        );
        break;
      }
    }

    if (!fieldTag) {
      return null;
    }

    return (
      <li key={fieldId} className="field">
        {fieldTag}
      </li>
    );
  }

  render() {
    return (
      <div className="cp-field-list">
        <h3>
          <FormattedMessage id="Facets.refineBy" />
          <small className="pull-right">
            <Drawer.Toggle drawerActions={this.props.drawerActions}>
              <TextButton handleClick={this.props.drawerActions.toggle} dataKey="field-list-button">
                <span className="icon icon-left-open" />
                Close
              </TextButton>
            </Drawer.Toggle>
          </small>
        </h3>
        <ul>{this.props.fieldsOrder.map(this.renderFields)}</ul>
      </div>
    );
  }
}

FieldList.propTypes = {
  activeFilters: ImmutablePropTypes.map.isRequired,
  fields: ImmutablePropTypes.list.isRequired,
  fieldsOrder: PropTypes.array.isRequired,
  handleExpandField: PropTypes.func,
  handleToggleFilter: PropTypes.func,
  handleToggleExpandedField: PropTypes.func,
  handleCloseExpandField: PropTypes.func,
  paginationField: PropTypes.string,
  renderSearchScope: PropTypes.func,
  transformQuery: PropTypes.func,
  singleBranch: PropTypes.bool,
  separator: PropTypes.string,
  prefix: PropTypes.string,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  router: routerShape.isRequired
};

FieldList.defaultProps = {
  paginationField: 'page',
  singleBranch: false
};

export default withRouter(FieldList);
